import 'please-wait/build/please-wait.css';
import 'Styles/shim.css';
// Import the services via the index file in that folder
import './services';
// Imports our global styles and bootstrap. This must be imported first before any components or
// other styles to ensure it comes first in the compiled css chunk
import 'Styles/custom.scss';
import './index.scss';
import 'Utilities/testHelper';

import moment from 'moment';
import { PleaseWait, pleaseWait } from 'please-wait';
import qs from 'querystring';
import React from 'react';
import { createRoot } from 'react-dom/client';
import { Provider } from 'react-redux';

import App from './App';
import { USER_LEGACY_TOKEN } from './reducers/auth/types';
import { ConfigState } from './reducers/config/types';

import { setUrls } from 'Utilities/dataHelpers';
import { _config, getConfig } from 'Utilities/envConfig';
import * as log from 'Utilities/log';
import mediator from 'Utilities/mediator';
import { guidRegExp } from 'Utilities/newyu';
import {
  clearCookie,
  clearSession,
  getCookie,
  getObject,
  getSessionObject,
  setCookie,
  setSessionObject,
} from 'Utilities/storage';
import { configureStore } from 'Utilities/store';

import ModalBase from 'Containers/ModalBase';

import ConfigVersionMessage from 'Components/utility/ConfigVersionMessage';
import UnexpectedError from 'Components/utility/UnexpectedError';

if (process.env.NODE_ENV !== 'production') {
  import('./dev');
}

let gotEnvConfig = false;
let loadingScreen: PleaseWait;
let store: ReturnType<typeof configureStore>;

let _errorReset = () => {
  //
};

const container = document.getElementById('app');
const root = createRoot(container!);

// Renders our UnexpectedError display and minimally bootstraps i18n
const renderError = (err: any) => {
  loadingScreen.finish();
  mediator.publish('init:i18n');

  window.setTimeout(() => {
    root.render(
      <div className="d-flex flex-column w-100 bg-white p-5" style={{ height: '100vh' }}>
        <div className="d-flex justify-content-between flex-wrap">
          {process.env.NODE_ENV !== 'production' && <span>{err.toString()}</span>}
          {gotEnvConfig && <ConfigVersionMessage />}
        </div>
        <UnexpectedError reset={_errorReset} />
      </div>
    );
  }, 0);
};

// Finally render our React trees
const renderApp = () => {
  try {
    loadingScreen.finish();

    root.render(
      <Provider store={store}>
        <App />
      </Provider>
    );

    const modelContainer = document.getElementById('modal-root');
    const modelRoot = createRoot(modelContainer!);

    modelRoot.render(
      <Provider store={store}>
        <ModalBase />
      </Provider>
    );
  } catch (e) {
    renderError(e);
    log.error(e);
  }
};

// Let our services know that the app is now ready to start
const startApp = () => {
  mediator.publish('store:created');
};

const hasTokenInQuery = () => {
  const querystring = (window.location.search.split('?')[1] || '').split('#')[0];
  const query = qs.decode(querystring);

  return !!query.token;
};

// Pull a user token from the url and save it into session storage
const loadTokenFromQuery = () => {
  const querystring = (window.location.search.split('?')[1] || '').split('#')[0];
  const query = qs.decode(querystring);

  if (query.token) {
    // Load the api url based on region
    if (query.region) {
      const { regionalMap } = store.getState().config;

      setUrls(regionalMap[query.region as string]);
    }

    setSessionObject('loggedIn', true);
    setSessionObject('token', {
      token: query.token,
      expires: moment()
        .add(5, 'minutes')
        .unix(),
    });

    const isLegacyToken = new RegExp(`^${guidRegExp}$`).test(query.token as string);

    if (isLegacyToken) {
      store.dispatch({ type: USER_LEGACY_TOKEN, token: query.token as string });
    }

    return true;
  }

  return false;
};

// Attempt to load a token from the url query on first try, otherwise start the app
const noCurrentSession = (firstTry: boolean) => {
  log.debug('no current session');

  const found = getObject('chosenCountryLang');

  if (firstTry && loadTokenFromQuery()) {
    // Load the ticket into the api service
    window.setTimeout(() => {
      mediator.publish('api:setTicket');
    });

    return;
  }

  if (found && found.country) {
    mediator.request('config:updateConfig', { country: found.country }).then(() => {
      // store.dispatch({
      //   type: COUNTRY_CHOSEN,
      //   ...found,
      // });

      startApp();
    });
  } else {
    startApp();
  }
};

// Attempt to get the user data based on session storage and fetch their config before starting the app
// fallsback to cookies if storage is empty/bad
const trySession = (firstTry = true) => {
  // For the first load we should prioritize the incomming token over any existing stored session
  // We would clear out the old session and load up from token instead
  if (firstTry && hasTokenInQuery()) {
    clearSession();
    clearCookie('loggedIn');
    noCurrentSession(true);
    return;
  }

  const loggedIn = getSessionObject('loggedIn') || getCookie('loggedIn');

  if (loggedIn) {
    mediator
      .request<{ user: User }>('user:getUser')
      .then((data) => {
        if (data.user.accountType !== 'pat') {
          log.info('Only patient accounts can be logged into ll-webviews');

          clearSession();
          clearCookie('loggedIn');
          noCurrentSession(true);
          return;
        }

        setSessionObject('loggedIn', { loggedIn: true });
        setCookie('loggedIn', true, 7200000);

        mediator
          .request('config:updateConfig', { country: data.user.country })
          .then(() => {
            startApp();
          })
          .catch(renderError);
      })
      .catch(() => {
        clearSession();
        clearCookie('loggedIn');
        noCurrentSession(false);
      });
  } else {
    // Server failed to get legacy token from FSLL
    if (window.location.pathname === '/tokenerror') {
      const querystring = (window.location.search.split('?')[1] || '').split('#')[0];
      const query = qs.decode(querystring);

      return renderError(
        new Error(`failed to get legacy token from redirect, status: ${query.code}`)
      );
    }

    noCurrentSession(firstTry);
  }
};

// We load the full config from server and then try to load a user session
const initialize = (config: typeof _config) => {
  setUrls(config);

  const found = getObject('chosenCountryLang');

  mediator
    .request<ConfigState>('config:updateConfig', { country: (found && found.country) || 'GB' })
    .then((res) => {
      store = configureStore({ config: res });
      setUrls(res);

      trySession();
    })
    .catch((err) => {
      log.error('failed to get server config: ' + err);
      renderError(new Error('failed to get server config: ' + err));
    });
};

// This is the starting point of our app launch life-cycle
const init = () => {
  loadingScreen = pleaseWait({
    backgroundColor: '#ffffff',
    loadingHtml:
      "<div class='sk-spinner sk-wave'><div class='sk-rect1'></div><div class='sk-rect2'></div><div class='sk-rect3'></div><div class='sk-rect4'></div><div class='sk-rect5'></div></div>",
    logo: '',
  });

  getConfig()
    .then((config) => {
      gotEnvConfig = true;
      log.init(config.logLevel);

      if (config.mock) {
        mediator.once('MOCK:setup:intialized', () => initialize(config));

        import('./services/mock');
      } else {
        initialize(config);
      }
    })
    .catch((err: Error) => {
      log.error('failed to get the environment config: ' + err);
      renderError(new Error('failed to get the environment config: ' + err));
    });
};

// Begin
init();

_errorReset = () => {
  init();
};

// Services that need to publish that they are ready
const appReady = {
  i18n: false,
  nav: false,
  router: false,
};

// Wait for the api to finish setting a ticket from query then try to start a session again
const tokenSub = mediator.once('app:queryTokenRead', () => trySession(false));

// Listens for all services to be complete so that we have everything we need when we mount React
const readySub = mediator.subscribe('app:ready', (ready: Partial<typeof appReady>) => {
  Object.assign(appReady, ready);

  const done = Object.values(appReady).every((value) => !!value);

  // i18n sets the document title, so we tell our router to 'refresh' to get the
  // document title to update on Android
  if (ready.i18n) {
    mediator.publish('init:router');
  }

  if (done) {
    mediator.publish('app:mounted');
    mediator.remove('app:queryTokenRead', tokenSub.id);
    mediator.remove('app:ready', readySub.id);
  }
});

mediator.once('app:mounted', renderApp);
